/**
 * TierStackedBar — score-box visual for the App Posture page where the
 * "score" is inventory composition rather than a 0-100 number. Renders one
 * row per tier with a label, a proportional segment bar, and the count.
 *
 * Critical segment carries a soft red glow per the mockup.
 */

export type Tier = 'Critical' | 'High' | 'Medium' | 'Low'

interface TierEntry {
  label: Tier
  count: number
}

interface TierStackedBarProps {
  tiers: TierEntry[]
  total: number
}

const TIER_FILL: Record<Tier, string> = {
  Critical: '#E57373',
  High: '#EBD4B4',
  Medium: '#A7D3F0',
  Low: '#76D69D',
}

const TIER_GLOW: Partial<Record<Tier, string>> = {
  Critical: '0 0 8px rgba(229,115,115,0.6)',
}

export default function TierStackedBar({ tiers, total }: TierStackedBarProps) {
  return (
    <div className="flex flex-col gap-2.5">
      {tiers.map(({ label, count }) => {
        const pct = total > 0 ? (count / total) * 100 : 0
        return (
          <div
            key={label}
            className="grid items-center gap-3"
            style={{ gridTemplateColumns: '80px 1fr 36px' }}
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/70">
              {label}
            </span>
            <span
              aria-hidden
              className="block h-4 overflow-hidden rounded-[4px] bg-white/[0.06]"
            >
              <span
                className="block h-full rounded-[4px]"
                style={{
                  width: `${Math.max(pct, count > 0 ? 4 : 0)}%`,
                  background: TIER_FILL[label],
                  boxShadow: TIER_GLOW[label],
                }}
              />
            </span>
            <span className="text-right text-[12px] font-semibold tabular-nums text-white">
              {count}
            </span>
          </div>
        )
      })}
    </div>
  )
}
